import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getAssetConfigTemplate } from '../../actions/tickets';
import ButtonScogoOrange from '../../common/Buttons/ButtonScogoOrange';
import ButtonScogoPrimary from '../../common/Buttons/ButtonScogoPrimary';
import { clickableTextClass, textCss } from '../../common/Css';
import Loader from '../../common/Loader';
import OverflowTip from '../../common/OverflowTip';
import { LineClampComponent } from './TicketTableComponent';
import IconToolTip from '../../common/IconToolTip';
import CountDown from '../../common/CountDown';
import NoResultFound from '../../common/NoResultFound';
import { openCustomModal } from '../../actions/modal';
import Assign from './Modal/Assign';
import { copyToClipboard } from '../../actions/utils';
import { acceptTicketMilestone, triggeredServiceRequest } from '../../actions/service_request';
import { motion, AnimatePresence } from 'framer-motion';
import { convertIntoRupee } from '../../utils/utils';
import { formName } from './Modal/Assign';
import NatsServiceAll from '../../utils/nats';
import { UPDATE_TRIGGERED_SERVICE_REQUEST_SUCCESS } from '../../types/service_request';

export default function NewTickets({ limitQueryParam, searchQueryParam }) {
    const dispatch = useDispatch();

    const newLoggedUserData = useSelector((state) => state.auth.newLoggedUserData);

    const isTriggeredServiceRequestLoading = useSelector((state) => state.loading.isTriggeredServiceRequestLoading);
    const isNextPageTriggeredServiceRequestLoading = useSelector((state) => state.loading.isNextPageTriggeredServiceRequestLoading);
    const triggeredServiceRequestList = useSelector((state) => state.serviceRequest.triggeredServiceRequestList);
    const isLastTriggeredServiceRequest = useSelector((state) => state.serviceRequest.isLastTriggeredServiceRequest);
    const [page, setPage] = useState(1);

    useEffect(() => {
        const assigned = `cloud.ticket.agent.assigned`;
        const user_assigned = `tickets.${newLoggedUserData.user.id}.assigned`;
        const broadcast = `tickets.${newLoggedUserData.user.id}.broadcasted`;
        NatsServiceAll.subscribe(assigned, (message) => {
            let data = JSON.parse(message)
            if (data.id) {
                dispatch({ type: UPDATE_TRIGGERED_SERVICE_REQUEST_SUCCESS, payload: { remove: data.id } })
            }
        });
        NatsServiceAll.subscribe(user_assigned, (message) => {
            let data = JSON.parse(message)
            if (data.ticket) {
                dispatch({ type: UPDATE_TRIGGERED_SERVICE_REQUEST_SUCCESS, payload: { add: data.ticket } })
            }
        });
        NatsServiceAll.subscribe(broadcast, (message) => {
            let data = JSON.parse(message)
            if (data.ticket) {
                dispatch({ type: UPDATE_TRIGGERED_SERVICE_REQUEST_SUCCESS, payload: { add: data.ticket } })
            }
        });

        return () => {
            NatsServiceAll.unsubscribe(assigned);
            NatsServiceAll.unsubscribe(user_assigned);
            NatsServiceAll.unsubscribe(broadcast);
        };
    }, [dispatch]);

    const getTicketList = useCallback(({ limit, page, query, payload }) => {
        dispatch(triggeredServiceRequest({ payload, limit, page, query }));
    }, [dispatch]);

    useEffect(() => {
        getTicketList({ limit: limitQueryParam, page: 1, query: searchQueryParam, payload: {} });
    }, [getTicketList, limitQueryParam, searchQueryParam]);

    if (isTriggeredServiceRequestLoading) {
        return (
            <div className="flex justify-center items-center h-full w-full">
                <Loader color='#F48A21' size='65' speed='1' thickness='3' />
            </div>
        );
    }

    if (triggeredServiceRequestList && triggeredServiceRequestList.length === 0 && !isTriggeredServiceRequestLoading) {
        return (
            <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5 }}
            >
                <NoResultFound message={'No tickets are available in your area, please check after sometime.'} />
            </motion.div>
        );
    }

    const handleScroll = (e) => {
        const bottom = Math.abs(e.target.scrollHeight - e.target.scrollTop - e.target.clientHeight) < 10;
        if (bottom && !isTriggeredServiceRequestLoading && !isNextPageTriggeredServiceRequestLoading && !isLastTriggeredServiceRequest) {
            getTicketList({ limit: limitQueryParam, page: page + 1, query: searchQueryParam, payload: {} });
            setPage(page + 1);
        }
    };

    return (
        <motion.div
            className={`w-full overflow-auto new_tickets_card_container nonPaginatedTable py-4 relative`}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.3 }}
            onScroll={handleScroll}
        >
            <div className={`grid w-full grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 px-4`}>
                <AnimatePresence>
                    {!isTriggeredServiceRequestLoading && triggeredServiceRequestList && triggeredServiceRequestList.map((ticket, index) => (
                        <motion.div
                            key={ticket.ticket_number || index}
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, scale: 0.95 }}
                            transition={{ duration: 0.3, delay: index * 0.05 }}
                            whileHover={{ scale: 1.02, transition: { duration: 0.2 } }}
                        >
                            <NewTicketCard ticket={ticket} />
                        </motion.div>
                    ))}
                </AnimatePresence>
            </div>
            {isNextPageTriggeredServiceRequestLoading && (
                <div
                    style={{
                        position: 'fixed',
                        left: 0,
                        right: 0,
                        bottom: 20,
                        display: 'flex',
                        justifyContent: 'center',
                        width: '100%',
                        pointerEvents: 'none',
                    }}
                >
                    <Loader color='#F48A21' size='40' speed='1' thickness='5' />
                </div>
            )}
        </motion.div>
    );
}


const NewTicketCard = ({ ticket }) => {
    const dispatch = useDispatch();
    const isDisableActions = false;
    const newLoggedUserData = useSelector(state => state.auth.newLoggedUserData);



    const onAccept = useCallback((agents) => {
        const milestoneIds = ticket.milestones.map(milestone => milestone.id);
        dispatch(acceptTicketMilestone({ ticketId: ticket.id, milestoneIds, payload: { agents }, formName, source: 'new_ticket' }));
    }, [dispatch, ticket.id, ticket.milestones]);

    const onAssignClick = (ticket) => {
        dispatch(openCustomModal({
            modalComponent: <Assign onAccept={onAccept} ticket={ticket} newTicketaccept agent={{ id: newLoggedUserData.user.id, full_name: newLoggedUserData.user.full_name }} />,
            heading: (
                <span className='text-black'>
                    Assign :&nbsp;<span className='text-scogobgsky text-font16'>{ticket.ticket_number}</span>
                </span>
            ),
            modalHeight: 'auto',
            modalWidth: '60rem'
        }));
    };

    const siteContactDetails = [
        ticket.job_site_contact_name,
        ticket.job_site_contact_mobile,
        ticket.job_site_contact_email
    ].filter(Boolean).join(' | ');

    const boqDetails = ticket?.boqDetails?.map((boq, index) =>
        `${boq.title} - ${boq.quantity}${ticket?.boqDetails?.length === index + 1 ? '' : ' | '}`
    );

    const getCardTruckColorCss = (status) => {
        const deliveryStatus = {
            delivered: 'DELIVERED',
            notInformation: 'No Information yet',
        };

        if (status === deliveryStatus.delivered) return 'scogosuccess';
        if (status === deliveryStatus.notInformation) return 'scogo99';
        return status ? 'scogoyellow' : '';
    };

    const countdownTimeDeadline = (broadcast_timestamp) => {
        const date = new Date(broadcast_timestamp);
        const hours = ticket.applicable_sla_in_seconds / 3600;
        date.setHours(date.getHours() + hours);
        return date;
    };

    const address = ticket.location && ticket.location.address ? ticket.location.address : '';

    const openLocation = () => {
        window.open(`https://maps.google.com?q=${address}`, '_blank');
    };

    const copyAddress = () => {
        dispatch(copyToClipboard({ data: address }));
    };

    const signoffSample = ticket.attributes && ticket.attributes.find(attribute => attribute.key === 'signoff_sample');
    const sow = ticket.attributes && ticket.attributes.find(attribute => attribute.key === 'sow');

    const spReward = useMemo(() => {
        let amount = 0;
        if (Array.isArray(ticket.milestones)) {
            ticket.milestones.forEach(({ payments, type }) => {
                amount += convertIntoRupee(payments.SP_SERVICE);
                // if (ticket.hasPsp === false && type !== MilestoneType.RMA && payments.PSP_INCENTIVE > 0) {
                //     amount += convertIntoRupee(payments.PSP_INCENTIVE);
                // }
            })
        }
        return amount * newLoggedUserData.wallet.ticket_payment_ratio;
    }, [newLoggedUserData.wallet.ticket_payment_ratio, ticket.milestones]);

    return (
        <motion.div
            className="bg-white relative shadow-md rounded-md border border-scogoprimary overflow-hidden"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            whileHover={{
                boxShadow: "0px 10px 20px rgba(0, 0, 0, 0.1)",
                y: -5,
                transition: { duration: 0.2 }
            }}
            style={{ height: '25rem' }}
        >
            <motion.div
                className="px-4 flex items-center justify-between py-3 relative rounded-t-lg w-full border-b-1 h-1/6"
                initial={{ borderColor: "#f3f3f3" }}
                animate={{ borderColor: "#3182ce" }}
                transition={{ duration: 0.5 }}
            >
                <div className='flex items-center w-4/5'>
                    <motion.p
                        className={`${textCss?.headerTwo} text-scogo11 font-medium uppercase truncate`}
                        whileHover={{ scale: 1.02 }}
                    >
                        <OverflowTip textClass={clickableTextClass} someLongText={ticket.ticket_number} />
                    </motion.p>
                </div>
            </motion.div>

            {/* Body */}
            <div className='px-4 overflow-auto h-4/6 py-2'>
                <div className='flex w-full items-center justify-between'>
                    <motion.div
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ delay: 0.1 }}
                    >
                        <LineClampComponent
                            textCss='text-font11 text-scogosuccess'
                            label={'Reward'}
                            value={`₹ ${spReward}`}
                            lineClamp='line-clamp-2'
                        />
                    </motion.div>

                    {ticket?.tracking_detail && (
                        <motion.div
                            initial={{ opacity: 0, scale: 0 }}
                            animate={{ opacity: 1, scale: 1 }}
                            transition={{ delay: 0.2, type: "spring" }}
                        >
                            <IconToolTip title='Delivered'>
                                <span className={`text-right material-icons text-2xl text-${getCardTruckColorCss(ticket?.tracking_detail?.current_status)}`}>
                                    local_shipping
                                </span>
                            </IconToolTip>
                        </motion.div>
                    )}
                </div>

                <motion.div
                    className="space-y-2"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 0.3 }}
                >
                    {ticket?.end_user?.name && (
                        <LineClampComponent label={'End User'} value={ticket.end_user?.name} lineClamp='line-clamp-2' />
                    )}
                    {ticket?.project?.name && (
                        <LineClampComponent label={'Project Name'} value={ticket.project?.name} lineClamp='line-clamp-2' />
                    )}

                    {ticket?.location?.external_id && (
                        <LineClampComponent label={'Site ID'} value={ticket?.location?.external_id} lineClamp='line-clamp-2' />
                    )}

                    <div className="flex flex-col">
                        <LineClampComponent label={'Site Address'} value={address || 'No address available'} lineClamp='line-clamp-2' />
                        {address ? <div className='flex gap-2 mt-1'>
                            <motion.div whileHover={{ scale: 1.2 }} whileTap={{ scale: 0.9 }}>
                                <IconToolTip placement={'bottom'} title={`Open Location`}>
                                    <span className='cursor-pointer material-icons text-scogobgsky text-lg' onClick={openLocation}>
                                        place
                                    </span>
                                </IconToolTip>
                            </motion.div>
                            <motion.div whileHover={{ scale: 1.2 }} whileTap={{ scale: 0.9 }}>
                                <IconToolTip placement={'bottom'} title={`Copy Address`}>
                                    <span className='cursor-pointer material-icons text-scogobgsky text-lg' onClick={copyAddress}>
                                        content_copy
                                    </span>
                                </IconToolTip>
                            </motion.div>
                        </div> : null}
                    </div>

                    {ticket.current_execution_date && (
                        <LineClampComponent
                            label={'Execution Date'}
                            value={moment(ticket.current_execution_date).format('DD MMM YYYY')}
                            lineClamp='line-clamp-2'
                        />
                    )}

                    {ticket.description && <LineClampComponent label='Description' value={ticket.description} />}
                    {boqDetails?.length > 0 && <LineClampComponent label='BOQ Details' value={boqDetails} />}
                    {siteContactDetails && <LineClampComponent label='Site Contact' value={siteContactDetails} lineClamp='line-clamp-2' />}
                    <LineClampComponent label={'Scogo PM'} value={ticket?.requester?.display_name || ''} lineClamp='line-clamp-2' />

                    <motion.div
                        className='flex gap-2 flex-wrap'
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.4 }}
                    >
                        {sow && <motion.span whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                            <IconToolTip title='Download SOW'>
                                <span onClick={() => window.open(sow.value.link, '_blank')}>
                                    <ButtonScogoOrange textOrComponent={'SOW'} />
                                </span>
                            </IconToolTip>
                        </motion.span>}

                        {signoffSample && <motion.span whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                            <IconToolTip title='Download SignOff Report'>
                                <span onClick={() => window.open(signoffSample.value.link, '_blank')}>
                                    <ButtonScogoOrange textOrComponent={'SignOff Report'} />
                                </span>
                            </IconToolTip>
                        </motion.span>}

                        {ticket.asset_config_required === 1 && (
                            <motion.span whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }}>
                                <IconToolTip title='Download Config Templates'>
                                    <span onClick={() => dispatch(getAssetConfigTemplate(ticket.ticket_id))}>
                                        <ButtonScogoOrange textOrComponent={'Config Templates'} />
                                    </span>
                                </IconToolTip>
                            </motion.span>
                        )}
                    </motion.div>
                </motion.div>
            </div>

            {/* Footer */}
            <motion.div
                className='h-1/6 flex justify-between px-4 items-center border-t-1'
                initial={{ borderColor: "#f3f3f3" }}
                animate={{ borderColor: "#3182ce" }}
                transition={{ duration: 0.5, delay: 0.5 }}
            >
                {ticket.applicable_sla_in_seconds && <CountDown deadline={countdownTimeDeadline(ticket.applicable_sla_in_seconds)} />}

                <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                >
                    <ButtonScogoPrimary
                        textOrComponent='Accept'
                        onClick={() => onAssignClick(ticket)}
                        disabled={isDisableActions}
                    />
                </motion.div>
            </motion.div>
        </motion.div>
    );
};

